<script setup lang="ts">
import { GpAvatar, GpBtn, GpCard } from '@gportal/uikit';
import CreateProjectForm from '@panel/components/project/create/CreateProjectForm.vue';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import { ref, useTemplateRef, watchEffect } from 'vue';
import WelcomeSection from './shared/WelcomeSection.vue';
import ContinueButton from './shared/ContinueButton.vue';
import WelcomeSectionBody from './shared/WelcomeSectionBody.vue';
import useSetupValidation from './composables/useSetupValidation';

const isValid = defineModel<boolean>('isValid', {
  default: false,
});

const emit = defineEmits<{
  nextButtonClicked: [];
}>();

const projectStore = useProjectStore();

const { userHasProjects } = useSetupValidation();

const createProjectForm =
  useTemplateRef<InstanceType<typeof CreateProjectForm>>('createProjectForm');
const isCreatingProject = ref(false);
const isFormValid = ref(false);
const createProject = async () => {
  if (!isFormValid.value) {
    return;
  }

  isCreatingProject.value = true;

  try {
    await createProjectForm.value.createProject();
  } catch (error) {
    // do nothing, error is handled in the form
  } finally {
    isCreatingProject.value = false;
  }
};

watchEffect(() => {
  if (userHasProjects.value) {
    isValid.value = true;
  }
});
</script>

<template>
  <WelcomeSection
    name="Create Project"
    :is-done="userHasProjects"
    v-playwright="'welcome-create-project-section'"
  >
    <WelcomeSectionBody v-if="userHasProjects">
      <template #default>
        <p>You have joined project</p>

        <GpCard class="tw-mt-4" :enlarge-on-hover="false" :border-on-hover="false">
          <template #content>
            <div class="tw-flex tw-items-center tw-gap-4 tw-p-4">
              <GpAvatar
                :src="projectStore.project.avatarUrl"
                :alt="projectStore.project.name"
                size="lg"
              />
              <div class="tw-flex-1">
                <p
                  class="tw-text-lg tw-font-bold"
                  v-playwright="'welcome-project-create-section-joined-project-name'"
                >
                  {{ projectStore.project.name }}
                </p>
              </div>
            </div>
          </template>
        </GpCard>
      </template>

      <template #footer>
        <ContinueButton
          class="tw-mx-auto"
          :disabled="!userHasProjects"
          v-playwright="'welcome-project-create-section-joined-project-continue'"
          @click="emit('nextButtonClicked')"
        />
      </template>
    </WelcomeSectionBody>

    <WelcomeSectionBody v-else>
      <template #default>
        <CreateProjectForm
          ref="createProjectForm"
          v-model:is-form-valid="isFormValid"
          :hide-create-button="true"
        />
      </template>

      <template #footer>
        <GpBtn
          :disabled="!isFormValid || isCreatingProject"
          class="tw-mx-auto tw-block"
          label="Create project"
          v-playwright="'welcome-create-project-section-create-project'"
          @click="createProject"
        />
      </template>
    </WelcomeSectionBody>
  </WelcomeSection>
</template>

<style scoped></style>
